import { useMemo, useState } from 'react'
import { matchPath, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { CopyDialog, DeleteConfirmDialog } from '@library'
import { DataGrid } from '@microservices/wiskey-react-table-component'
import { DRAFT_COLUMN, MOBILE_LIST_ITEMS_COLUMNS } from '@mobileConstants'
import { exportToJSON } from '@mobileHelpers'
import { useAllDrafts, useMobileListItems } from '@mobileHooks'
import { ConfigListPageShell } from '@mobileComponents/ConfigListPageShell'
import { MobileListItem } from '@mobileTypes'
import { Add as AddIcon } from '@mui/icons-material'
import { Box, Button } from '@mui/material'

import { generateDataGridActionsColumn } from '@helpers'
import { ROUTES } from '@constants'

export const ConfigMobileListItems = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const isAllListItems = matchPath(ROUTES.CONFIG_MOBILE_LIST_ITEMS, pathname)

  const { listItems, loading, copyListItem, deleteListItem } = useMobileListItems()
  const { entries: draftEntries, deleteDraft } = useAllDrafts<MobileListItem>('mobile-list-item-draft-')

  const [copyDialogOpen, setCopyDialogOpen] = useState(false)
  const [itemToCopy, setItemToCopy] = useState<{ id: number; internalId: string; title: string } | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [itemToDelete, setItemToDelete] = useState<{ id: number; title: string } | null>(null)

  const handleCreate = () => navigate(ROUTES.CONFIG_MOBILE_LIST_ITEMS_CREATE)

  const handleEdit = (id: number) => navigate(`${ROUTES.CONFIG_MOBILE_LIST_ITEMS_EDIT}/${id}`)

  const handleCopyClick = (id: number) => {
    const item = listItems.find(li => li.id === id)
    if (item) {
      setItemToCopy({ id: item.id, internalId: item.internalId, title: item.title })
      setCopyDialogOpen(true)
    }
  }

  const handleCopy = async (newName: string, newInternalId: string) => {
    if (itemToCopy) {
      const newItem = await copyListItem(itemToCopy.id, newName, newInternalId)
      if (newItem) {
        setCopyDialogOpen(false)
        setItemToCopy(null)
        navigate(`${ROUTES.CONFIG_MOBILE_LIST_ITEMS_EDIT}/${newItem.id}`)
      }
    }
  }

  const handleDeleteClick = (id: number) => {
    const item = listItems.find(li => li.id === id)
    if (item) {
      setItemToDelete({ id: item.id, title: item.title })
      setDeleteDialogOpen(true)
    }
  }

  const handleDelete = () => {
    if (itemToDelete) {
      deleteListItem(itemToDelete.id)
      setDeleteDialogOpen(false)
      setItemToDelete(null)
    }
  }

  const handleExport = (internalId: string) => {
    const item = listItems.find(li => li.internalId === internalId)
    if (item) exportToJSON(item, `${item.title}_list_item_config.json`)
  }

  const rows = useMemo(() => {
    const draftEntityIdMap = new Map(
      draftEntries.filter(e => e.idPart !== 'new').map(e => [e.idPart, e]),
    )

    const newDraftRows = draftEntries
      .filter(e => e.idPart === 'new')
      .map(e => ({
        ...e.draft.data,
        id: 'draft-new',
        _isDraft: true,
        _isNewDraft: true,
        _draftKey: e.key,
      }))

    const entityDraftRows = listItems
      .filter(li => draftEntityIdMap.has(String(li.id)))
      .map(li => {
        const entry = draftEntityIdMap.get(String(li.id))!
        return { ...li, ...entry.draft.data, id: li.id, _isDraft: true, _isNewDraft: false, _draftKey: entry.key }
      })

    const serverRows = listItems.filter(li => !draftEntityIdMap.has(String(li.id)))

    return [...newDraftRows, ...entityDraftRows, ...serverRows]
  }, [listItems, draftEntries])

  const columns = [
    DRAFT_COLUMN,
    ...MOBILE_LIST_ITEMS_COLUMNS,
    generateDataGridActionsColumn({
      onEdit: (params: any) => {
        if (params.row._isNewDraft) {
          navigate(ROUTES.CONFIG_MOBILE_LIST_ITEMS_CREATE, { state: { restoreDraft: true } })
        } else if (params.row._isDraft) {
          navigate(`${ROUTES.CONFIG_MOBILE_LIST_ITEMS_EDIT}/${params.row.id}`, { state: { restoreDraft: true } })
        } else {
          handleEdit(params.row.id)
        }
      },
      onCopy: (params: any) => {
        if (!params.row._isNewDraft) handleCopyClick(params.row.id)
      },
      onDelete: (params: any) => {
        if (params.row._isDraft) {
          deleteDraft(params.row._draftKey)
        } else {
          handleDeleteClick(params.row.id)
        }
      },
      onDowloadButtonClick: (params: any) => {
        if (!params.row._isNewDraft) handleExport(params.row.internalId)
      },
    }),
  ]

  return (
    <>
      <Outlet />

      <CopyDialog
        open={copyDialogOpen}
        title='Copy List Item Configuration'
        initialName={itemToCopy?.title || ''}
        existingNames={listItems.map(li => li.title)}
        initialInternalId={itemToCopy?.internalId}
        existingInternalIds={listItems.map(li => li.internalId)}
        onClose={() => { setCopyDialogOpen(false); setItemToCopy(null) }}
        onCopy={handleCopy}
      />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title='Delete List Item Configuration'
        message='Are you sure you want to delete list item'
        itemName={itemToDelete?.title}
        onClose={() => { setDeleteDialogOpen(false); setItemToDelete(null) }}
        onConfirm={handleDelete}
      />

      <ConfigListPageShell
        title='Mobile List Items'
        isVisible={!!isAllListItems}
        actions={
          <Button variant='contained' startIcon={<AddIcon />} onClick={handleCreate}>
            Create
          </Button>
        }
      >
          <Box sx={{ display: 'flex', flex: 1 }}>
            <DataGrid rows={rows} columns={columns} loading={loading} hideFooter />
          </Box>
      </ConfigListPageShell>
    </>
  )
}
